import React, { useEffect, useState, useRef } from 'react'

import clsx from 'clsx'
import ReactDOM from 'react-dom'

import { useTransition, useSimplePopper } from 'hooks'

import classes from './Tooltip.module.scss'

const noop = () => {}

/** @type {import("./types").default} */
const Tooltip = ({
  title,
  children,
  className,
  placement = 'bottom',
  enterTouchDelay = 700,
  enterDelay = 100,
  leaveDelay = 0,
  leaveTouchDelay = 1500,
  sendOnEnterEvents = noop,
  onTooltipClose = noop,
  setOpen: externalSetOpen,
}) => {
  const targetRef = useRef(null)
  const tooltipRef = useRef(null)
  const [isOpen, setIsOpen] = useState(false)
  const { shouldMount, stage } = useTransition(isOpen, 300)

  const { styles } = useSimplePopper(targetRef.current, tooltipRef.current, {
    placement,
    strategy: 'absolute',
  })

  const timers = useRef({
    enter: null,
    leave: null,
    touch: null,
  })
  const isTouchInteraction = useRef(false)

  useEffect(() => {
    const currentTimers = timers.current
    return () => {
      clearTimeout(currentTimers.enter)
      clearTimeout(currentTimers.leave)
      clearTimeout(currentTimers.touch)
    }
  }, [])

  // Allow external control of open state
  useEffect(() => {
    if (typeof externalSetOpen === 'function') {
      externalSetOpen(setIsOpen)
    }
  }, [externalSetOpen])

  const handleOpen = event => {
    if (isTouchInteraction.current && event?.type !== 'touchstart') return

    clearTimeout(timers.current.enter)
    clearTimeout(timers.current.leave)

    const delay = event?.type === 'touchstart' ? enterTouchDelay : enterDelay

    timers.current.enter = setTimeout(() => {
      setIsOpen(true)
      sendOnEnterEvents()
    }, delay)
  }

  const handleClose = () => {
    clearTimeout(timers.current.enter)
    clearTimeout(timers.current.leave)

    const delay = isTouchInteraction.current ? leaveTouchDelay : leaveDelay

    timers.current.leave = setTimeout(() => {
      setIsOpen(false)
      isTouchInteraction.current = false
      onTooltipClose && onTooltipClose()
    }, delay)
  }

  const handleTouchStart = event => {
    isTouchInteraction.current = true
    clearTimeout(timers.current.touch)
    handleOpen(event)
  }

  const childProps = {
    ref: targetRef,
    onFocus: handleOpen,
    onBlur: handleClose,
    onMouseOver: handleOpen,
    onMouseLeave: handleClose,
    onTouchStart: handleTouchStart,
    onTouchEnd: handleClose,
  }

  return (
    <>
      {React.isValidElement(children) && React.cloneElement(children, childProps)}
      {shouldMount &&
        title &&
        ReactDOM.createPortal(
          <div ref={tooltipRef} className={classes.floating} style={styles.popper}>
            <div
              className={clsx(classes.floatingContent, classes[stage], className, {
                [classes.touch]: isTouchInteraction.current,
              })}
            >
              {title}
            </div>
          </div>,
          document?.getElementById('portal_root') || document.body
        )}
    </>
  )
}

export default Tooltip
